import {useEffect, useMemo, useRef, useState, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {
  Activity,
  BarChart3,
  Box,
  ChevronDown,
  CircleDollarSign,
  Download,
  Eye,
  KeyRound,
  LogOut,
  LockKeyhole,
  Layers3,
  MoreHorizontal,
  Package,
  PackagePlus,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Ticket,
  Trash2,
  UploadCloud,
  Users,
  X, Archive,
} from 'lucide-react'
import {PreviewImages} from './components/MediaFields'
import {PluginEditorPage, type EditingPlugin} from './components/PluginEditorPage'
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiUpload,
  changePassword,
  clearAdminToken,
  getAdminUsername,
  getAdminToken,
  login,
  setAdminUsername,
  setAdminToken,
  type ActivationCode,
  type Authorization,
  type DeviceAccess,
  type PluginAuditLog,
  type Plugin,
  type PluginVersion,
} from './api'

type Toast = {
  kind: 'success' | 'error'
  text: string
}

type MenuKey = 'plugins' | 'auth' | 'devices'
type DetailTab = 'detail' | 'versions' | 'codes'

const categoryOptions = [
  {value: 'ai', label: 'AI工具'},
  {value: 'ui', label: '界面'},
  {value: 'utility', label: '实用工具'},
  {value: 'commerce', label: '电商'},
  {value: 'seo', label: 'SEO'},
]

const emptyPlugin: EditingPlugin = {
  id: '',
  name: '',
  author: '官方',
  category: 'utility',
  group: 'utility',
  runtime: 'process',
  summary: '',
  description: '',
  status: 'draft',
  billingType: 'free',
  icon: '',
  previews: '',
}

export function App() {
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [username, setUsername] = useState(getAdminUsername())
  const [password, setPassword] = useState('')
  const [authed, setAuthed] = useState(Boolean(getAdminToken()))
  const [activeMenu, setActiveMenu] = useState<MenuKey>('plugins')
  const [plugins, setPlugins] = useState<Plugin[]>([])
  const [pluginSearch, setPluginSearch] = useState('')
  const [selectedID, setSelectedID] = useState('')
  const [drawerPluginID, setDrawerPluginID] = useState('')
  const [actionsMenuPluginID, setActionsMenuPluginID] = useState('')
  const [detailTab, setDetailTab] = useState<DetailTab>('detail')
  const [versions, setVersions] = useState<PluginVersion[]>([])
  const [allAuthorizations, setAllAuthorizations] = useState<Authorization[]>([])
  const [authorizationPage, setAuthorizationPage] = useState(1)
  const [devices, setDevices] = useState<DeviceAccess[]>([])
  const [selectedDevice, setSelectedDevice] = useState<DeviceAccess | null>(null)
  const [deviceLogs, setDeviceLogs] = useState<PluginAuditLog[]>([])
  const [activationCodes, setActivationCodes] = useState<ActivationCode[]>([])
  const [activationCodePage, setActivationCodePage] = useState(1)
  const [editingPlugin, setEditingPlugin] = useState({...emptyPlugin})
  const [editorOpen, setEditorOpen] = useState(false)
  const [codeCount, setCodeCount] = useState('10')
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
  const [passwordForm, setPasswordForm] = useState({oldPassword: '', newPassword: '', confirmPassword: ''})
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<Toast | null>(null)

  const drawerPlugin = useMemo(() => plugins.find((item) => item.id === drawerPluginID), [plugins, drawerPluginID])
  const visiblePlugins = useMemo(() => {
    const query = pluginSearch.trim().toLowerCase()
    if (!query) return plugins
    return plugins.filter((plugin) => [
      plugin.name,
      plugin.id,
      plugin.author || '官方',
      categoryLabel(plugin.group || plugin.category),
      plugin.runtime,
      plugin.summary || '',
      plugin.description || '',
    ].some((value) => value.toLowerCase().includes(query)))
  }, [pluginSearch, plugins])
  const authorizationPageSize = 5
  const activationCodePageSize = 5
  const authorizationPageCount = Math.max(1, Math.ceil(allAuthorizations.length / authorizationPageSize))
  const activationCodePageCount = Math.max(1, Math.ceil(activationCodes.length / activationCodePageSize))
  const pagedAuthorizations = allAuthorizations.slice((authorizationPage - 1) * authorizationPageSize, authorizationPage * authorizationPageSize)
  const pagedActivationCodes = activationCodes.slice((activationCodePage - 1) * activationCodePageSize, activationCodePage * activationCodePageSize)

  function notify(kind: Toast['kind'], text: string) {
    setToast({kind, text})
    window.setTimeout(() => setToast(null), 2400)
  }

  async function doLogin() {
    try {
      const res = await login(username, password)
      setAdminToken(res.token)
      setAdminUsername(res.username || username)
      setUsername(res.username || username)
      setAuthed(true)
      notify('success', '登录成功')
      await loadPlugins()
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '登录失败')
    }
  }

  async function loadPlugins() {
    setLoading(true)
    try {
      const res = await apiGet<{success: boolean; plugins: Plugin[]}>('/plugins')
      const next = res.plugins || []
      setPlugins(next)
      if (!selectedID && next.length) setSelectedID(next[0].id)
      if (drawerPluginID && !next.some((item) => item.id === drawerPluginID)) closeDrawer()
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '加载失败')
    } finally {
      setLoading(false)
    }
  }

  async function loadDetail(pluginID: string) {
    if (!pluginID) {
      setVersions([])
      setActivationCodes([])
      setActivationCodePage(1)
      return
    }
    try {
      const [versionRes, codeRes] = await Promise.all([
        apiGet<{success: boolean; versions: PluginVersion[]}>(`/plugins/${pluginID}/versions`),
        apiGet<{success: boolean; codes: ActivationCode[]}>(`/activation-codes?pluginId=${encodeURIComponent(pluginID)}`),
      ])
      setVersions(versionRes.versions || [])
      setActivationCodes(codeRes.codes || [])
      setActivationCodePage(1)
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '加载详情失败')
    }
  }

  async function loadAllAuthorizations() {
    try {
      const res = await apiGet<{success: boolean; authorizations: Authorization[]}>('/authorizations')
      setAllAuthorizations(res.authorizations || [])
      setAuthorizationPage(1)
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '加载授权失败')
    }
  }

  async function loadDevices() {
    try {
      const res = await apiGet<{success: boolean; devices: DeviceAccess[]}>('/devices')
      setDevices(res.devices || [])
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '加载设备记录失败')
    }
  }

  async function openDevice(device: DeviceAccess) {
    setSelectedDevice(device)
    try {
      const res = await apiGet<{success: boolean; logs: PluginAuditLog[]}>(`/devices/${encodeURIComponent(device.deviceId)}/audit-logs`)
      setDeviceLogs(res.logs || [])
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '加载设备操作记录失败')
      setDeviceLogs([])
    }
  }

  useEffect(() => {
    if (authed) loadPlugins().catch(() => {})
  }, [authed])

  useEffect(() => {
    if (drawerPluginID) loadDetail(drawerPluginID).catch(() => {})
  }, [drawerPluginID])

  useEffect(() => {
    if (authed && activeMenu === 'auth') loadAllAuthorizations().catch(() => {})
  }, [authed, activeMenu])

  useEffect(() => {
    if (authed && activeMenu === 'devices') loadDevices().catch(() => {})
  }, [authed, activeMenu])

  function openEditor(plugin?: Plugin) {
    setEditingPlugin(plugin ? {
      id: plugin.id,
      name: plugin.name,
      author: plugin.author || '官方',
      category: plugin.category || 'utility',
      group: plugin.group || plugin.category || 'utility',
      runtime: plugin.runtime,
      summary: plugin.summary || '',
      description: plugin.description || '',
      status: plugin.status,
      billingType: plugin.billingType || 'free',
      icon: plugin.icon || '',
      previews: plugin.previews || '',
    } : {...emptyPlugin})
    setEditorOpen(true)
  }

  function openDrawer(plugin: Plugin, tab: DetailTab = 'detail') {
    setDrawerPluginID(plugin.id)
    setDetailTab(tab)
  }

  function closeDrawer() {
    setDrawerPluginID('')
    setVersions([])
    setActivationCodes([])
  }

  async function savePlugin() {
    try {
      await apiPost('/plugins', editingPlugin)
      notify('success', '插件已保存')
      setEditorOpen(false)
      await loadPlugins()
      setSelectedID(editingPlugin.id)
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '保存失败')
    }
  }

  async function deletePlugin(plugin: Plugin) {
    if (!window.confirm(`删除插件 ${plugin.name}？`)) return
    try {
      await apiDelete(`/plugins/${plugin.id}`)
      notify('success', '插件已删除')
      if (selectedID === plugin.id) setSelectedID('')
      if (drawerPluginID === plugin.id) closeDrawer()
      await loadPlugins()
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '删除失败')
    }
  }

  async function setStatus(plugin: Plugin, status: 'listed' | 'unlisted') {
    try {
      await apiPatch(`/plugins/${plugin.id}/status`, {status})
      notify('success', status === 'listed' ? '插件已上架' : '插件已下架')
      await loadPlugins()
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '操作失败')
    }
  }

  async function setVersionStatus(version: string, status: 'published' | 'unlisted') {
    if (!drawerPlugin) return
    try {
      await apiPatch(`/plugins/${drawerPlugin.id}/versions/${encodeURIComponent(version)}/status`, {status})
      notify('success', status === 'published' ? '版本已发布' : '版本已下架')
      await loadDetail(drawerPlugin.id)
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '操作失败')
    }
  }

  async function deleteVersion(version: string) {
    if (!drawerPlugin || !window.confirm(`删除版本 ${version}？`)) return
    try {
      await apiDelete(`/plugins/${drawerPlugin.id}/versions/${encodeURIComponent(version)}`)
      notify('success', '版本已删除')
      await loadDetail(drawerPlugin.id)
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '删除失败')
    }
  }

  async function upload(file?: File) {
    if (!file) return
    try {
      const res = await apiUpload<{success: boolean; version: PluginVersion}>('/plugins/versions/upload', file)
      notify('success', `已上传 ${res.version.pluginId} v${res.version.version}`)
      await loadPlugins()
      setDrawerPluginID(res.version.pluginId)
      setDetailTab('versions')
      await loadDetail(res.version.pluginId)
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '上传失败')
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function generateCodes() {
    if (!drawerPlugin) return
    try {
      const count = Math.max(1, Math.min(200, Number(codeCount) || 1))
      await apiPost(`/plugins/${drawerPlugin.id}/activation-codes`, {count, createdBy: 'admin'})
      notify('success', `已生成 ${count} 个激活码`)
      await loadDetail(drawerPlugin.id)
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '生成失败')
    }
  }

  async function revokeAuthorization(pluginID: string, deviceID: string) {
    if (!pluginID || !deviceID) return
    if (!window.confirm(`确认取消设备 ${deviceID} 的 ${pluginID} 授权？`)) return
    try {
      await apiDelete(`/plugins/${pluginID}/authorizations/${encodeURIComponent(deviceID)}`)
      notify('success', '授权已取消')
      if (drawerPluginID === pluginID) await loadDetail(pluginID)
      await loadAllAuthorizations()
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '取消授权失败')
    }
  }

  function logout() {
    clearAdminToken()
    setAuthed(false)
    setPassword('')
    setAccountMenuOpen(false)
    setPasswordModalOpen(false)
    closeDrawer()
    notify('success', '已退出登录')
  }

  async function savePassword() {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      notify('error', '两次输入的新密码不一致')
      return
    }
    try {
      const res = await changePassword(passwordForm.oldPassword, passwordForm.newPassword)
      if (res.username) {
        setUsername(res.username)
        setAdminUsername(res.username)
      }
      setPasswordForm({oldPassword: '', newPassword: '', confirmPassword: ''})
      setPasswordModalOpen(false)
      setAccountMenuOpen(false)
      notify('success', '密码已修改')
    } catch (err) {
      notify('error', err instanceof Error ? err.message : '修改失败')
    }
  }

  if (!authed) {
    return (
      <main className="login-page">
        {toast ? <div className={`toast ${toast.kind}`}>{toast.text}</div> : null}
        <section className="login-shell">
          <div className="login-showcase">
            <span className="login-kicker">WXBot Ecosystem</span>
            <h1>让插件发布、授权与交付变得清晰。</h1>
            <p>集中管理插件生命周期、跨平台构建产物和商业授权。</p>
            <div className="login-feature-list">
              <span><Package size={18} /> 版本与平台产物</span>
              <span><ShieldCheck size={18} /> 授权与激活码</span>
              <span><BarChart3 size={18} /> 市场表现概览</span>
            </div>
          </div>
          <div className="login-panel">
            <div className="brand login-brand">
              <Box size={26} />
              <div>
                <h1>插件管理后台</h1>
                <span>Plugin Admin Console</span>
              </div>
            </div>
            <div className="login-copy">
              <h2>欢迎回来</h2>
              <p>登录后继续管理你的插件生态。</p>
            </div>
            <label className="field-label"><span>管理员账号</span><input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="请输入账号" autoComplete="username" /></label>
            <label className="field-label"><span>管理员密码</span><input value={password} onChange={(event) => setPassword(event.target.value)} placeholder="请输入密码" type="password" autoComplete="current-password" onKeyDown={(event) => { if (event.key === 'Enter') void doLogin() }} /></label>
            <button className="primary login-submit" onClick={doLogin}><KeyRound size={16} /> 登录控制台</button>
            <div>
              <span className="login-security"><LockKeyhole size={13} /> 管理会话仅保存在当前设备</span>
            </div>
          </div>
        </section>
      </main>
    )
  }

  return (
    <main className="app">
      {toast ? <div className={`toast ${toast.kind}`}>{toast.text}</div> : null}
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark"><Package size={20} /></span>
          <div>
            <h1>插件管理后台</h1>
            <span>Plugin Admin</span>
          </div>
        </div>
        <nav className="main-menu">
          <span className="nav-label">工作台</span>
          <button className={activeMenu === 'plugins' ? 'active' : ''} onClick={() => setActiveMenu('plugins')}>
            <PackagePlus size={17} />
            <span>插件管理</span>
          </button>
          <button className={activeMenu === 'auth' ? 'active' : ''} onClick={() => setActiveMenu('auth')}>
            <ShieldCheck size={17} />
            <span>授权管理</span>
          </button>
          <button className={activeMenu === 'devices' ? 'active' : ''} onClick={() => setActiveMenu('devices')}>
            <Activity size={17} />
            <span>设备访问</span>
          </button>
        </nav>
        <button className="upload" onClick={() => fileRef.current?.click()}>
          <UploadCloud size={18} /> 上传插件包
        </button>
        <input ref={fileRef} type="file" accept=".wxplugin,.zip,application/zip" hidden onChange={(event) => upload(event.target.files?.[0])} />
        <div className="sidebar-foot">
          <span className="system-dot" />
          <div><strong>服务运行正常</strong><span>Plugin Admin v1</span></div>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div className="breadcrumb">
            <span className="breadcrumb-icon">{activeMenu === 'plugins' ? <PackagePlus size={17} /> : activeMenu === 'auth' ? <ShieldCheck size={17} /> : <Activity size={17} />}</span>
            <div><strong>{activeMenu === 'plugins' ? '插件管理' : activeMenu === 'auth' ? '授权管理' : '设备访问'}</strong><em>{activeMenu === 'plugins' ? '管理插件、版本与市场状态' : activeMenu === 'auth' ? '控制插件的设备授权' : '记录插件市场设备访问情况'}</em></div>
          </div>
          <div className="header-actions">
            <div className="account-menu">
              <button className="account-trigger" onClick={() => setAccountMenuOpen((open) => !open)}>
                <span className="avatar">{avatarText(username)}</span>
                <span>{username}</span>
                <ChevronDown size={15} />
              </button>
              {accountMenuOpen ? (
                <div className="account-dropdown">
                  <button onClick={() => { setPasswordModalOpen(true); setAccountMenuOpen(false) }}>
                    <LockKeyhole size={15} /> 修改密码
                  </button>
                  <button onClick={logout}>
                    <LogOut size={15} /> 退出
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <section className="content">
        {activeMenu === 'plugins' ? (
          editorOpen ? (
            <PluginEditorPage
              plugin={editingPlugin}
              categoryOptions={categoryOptions}
              onChange={setEditingPlugin}
              onCancel={() => setEditorOpen(false)}
              onSave={savePlugin}
            />
          ) : (
          <>
            <div className="page-heading">
              <div>
                <h2>插件资产</h2>
                <p>查看发布状态、商业模式与市场表现。</p>
              </div>
              <div className="page-actions">
                <button className="secondary" disabled={loading} onClick={loadPlugins}>
                  <RefreshCw size={15} className={loading ? 'spin' : ''} /> 刷新
                </button>
                <button className="primary" onClick={() => openEditor()}><Plus size={15} /> 新建插件</button>
              </div>
            </div>
            <div className="overview-grid">
              <MetricCard icon={<Layers3 size={19} />} tone="indigo" label="插件总数" value={plugins.length} hint={`${plugins.filter((item) => item.status === 'listed').length} 个已上架`} />
              <MetricCard icon={<Activity size={19} />} tone="emerald" label="上架中" value={plugins.filter((item) => item.status === 'listed').length} hint="市场可见" />
              <MetricCard icon={<CircleDollarSign size={19} />} tone="amber" label="收费插件" value={plugins.filter((item) => item.billingType === 'paid').length} hint="已启用商业授权" />
              <MetricCard icon={<Download size={19} />} tone="blue" label="累计下载" value={plugins.reduce((sum, item) => sum + (item.downloadCount || 0), 0)} hint="全部插件累计" />
            </div>
            <section className="panel plugin-list-panel">
            <div className="panel-toolbar">
              <div><h3>全部插件</h3><p>{visiblePlugins.length} 个结果</p></div>
              <div className="search-box">
                <Search size={16} />
                <input value={pluginSearch} onChange={(event) => setPluginSearch(event.target.value)} placeholder="搜索名称、ID、作者或分组" />
              </div>
            </div>
            <Table
              headers={['插件', '类型', '状态', '商业模式', '市场表现', '更新时间', '']}
              rows={visiblePlugins.map((plugin) => [
                <div className="plugin-cell">
                  {plugin.icon ? <img src={plugin.icon} alt="" /> : <span className="plugin-cell-icon"><Package size={16} /></span>}
                  <span><strong>{plugin.name}</strong><em>{plugin.summary || plugin.description || '-'}</em></span>
                </div>,
                <div className="type-cell"><strong>{categoryLabel(plugin.group || plugin.category)}</strong><span>{plugin.runtime} · {plugin.author || '官方'}</span></div>,
                <Status status={plugin.status} />,
                <span className={`billing-pill ${plugin.billingType}`}>{plugin.billingType === 'paid' ? '收费' : '免费'}</span>,
                <div className="performance-cell"><strong>{(plugin.ratingAverage || 5).toFixed(1)} 分</strong><span>{plugin.downloadCount || 0} 次下载</span></div>,
                formatTime(plugin.updatedAt),
                <PluginActionsMenu
                  plugin={plugin}
                  open={actionsMenuPluginID === plugin.id}
                  onToggle={() => setActionsMenuPluginID((current) => current === plugin.id ? '' : plugin.id)}
                  onClose={() => setActionsMenuPluginID('')}
                  onDetail={() => openDrawer(plugin)}
                  onEdit={() => openEditor(plugin)}
                  onSetStatus={() => setStatus(plugin, nextPluginStatus(plugin.status))}
                  onDelete={() => deletePlugin(plugin)}
                />,
              ])}
            />
            </section>
          </>
          )
        ) : activeMenu === 'auth' ? (
          <>
            <div className="page-heading">
              <div>
                <h2>授权管理</h2>
                <p>所有插件均按部署设备 ID 授权</p>
              </div>
              <div className="page-actions">
                <button className="secondary" disabled={loading} onClick={loadAllAuthorizations}>
                  <RefreshCw size={15} /> 刷新
                </button>
              </div>
            </div>
            <div className="overview-grid auth-overview">
              <MetricCard icon={<Users size={19} />} tone="indigo" label="授权记录" value={allAuthorizations.length} hint="全部授权范围" />
              <MetricCard icon={<ShieldCheck size={19} />} tone="emerald" label="有效授权" value={allAuthorizations.filter((item) => item.status === 'active').length} hint="当前处于启用状态" />
              <MetricCard icon={<Package size={19} />} tone="blue" label="覆盖插件" value={new Set(allAuthorizations.map((item) => item.pluginId)).size} hint="已有授权记录" />
            </div>
            <section className="panel">
            <div className="panel-toolbar compact"><div><h3>授权记录</h3><p>{allAuthorizations.length} 条记录</p></div></div>
            <Table
              className="authorization-table"
              headers={['插件', '授权方式', '授权对象', '来源', '状态', '更新时间', '操作']}
              rows={pagedAuthorizations.map((item) => [
                item.pluginId,
                scopeLabel(item.scopeType),
                <span className="authorization-subject" title={item.scopeKey}>{item.scopeKey}</span>,
                sourceLabel(item.source),
                item.status === 'active' ? '启用' : '禁用',
                formatTime(item.updatedAt),
                item.status === 'active' ? <button className="danger table-danger" onClick={() => revokeAuthorization(item.pluginId, item.scopeKey)}>取消授权</button> : '-',
              ])}
            />
            <Pagination page={authorizationPage} pageCount={authorizationPageCount} total={allAuthorizations.length} onChange={setAuthorizationPage} />
            </section>
          </>
        ) : (
          <>
            <div className="page-heading">
              <div><h2>设备访问</h2><p>设备信息记录首次采集，访问时间随插件市场请求更新。</p></div>
              <div className="page-actions"><button className="secondary" onClick={loadDevices}><RefreshCw size={15} /> 刷新</button></div>
            </div>
            <div className="overview-grid">
              <MetricCard icon={<Activity size={19} />} tone="indigo" label="设备总数" value={devices.length} hint="已请求插件市场的设备" />
              <MetricCard icon={<ShieldCheck size={19} />} tone="emerald" label="最近访问设备" value={devices.length ? 1 : 0} hint={devices.length ? formatTime(devices[0].lastAccess) : '暂无访问记录'} />
            </div>
            <section className="panel plugin-list-panel">
              <div className="panel-toolbar"><div><h3>设备记录</h3><p>{devices.length} 台设备</p></div></div>
              <Table
                headers={['设备 ID', '系统', '首次 IP', '首次访问', '上次访问', 'User-Agent']}
                rows={devices.map((device) => [
                  <button className="link-button" onClick={() => openDevice(device)}>{device.deviceId}</button>,
                  `${device.os || '-'} / ${device.arch || '-'}`,
                  device.firstIp || '-',
                  formatTime(device.firstAccess),
                  formatTime(device.lastAccess),
                  <span title={device.userAgent}>{device.userAgent || '-'}</span>,
                ])}
              />
            </section>
          </>
        )}
        </section>
      </section>

      {selectedDevice ? (
        <div className="modal-backdrop" onMouseDown={() => setSelectedDevice(null)}>
          <section className="modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><h3>设备详情</h3><p>{selectedDevice.deviceId}</p></div><button className="icon-button" onClick={() => setSelectedDevice(null)}><X size={18} /></button></div>
            <div className="detail-grid">
              <div><dt>系统</dt><dd>{selectedDevice.os || '-'} {selectedDevice.osVersion || ''}</dd></div>
              <div><dt>架构</dt><dd>{selectedDevice.arch || '-'}</dd></div>
              <div><dt>内核</dt><dd>{selectedDevice.kernelVersion || '-'}</dd></div>
              <div><dt>应用版本</dt><dd>{selectedDevice.appVersion || '-'}</dd></div>
              <div><dt>Go 版本</dt><dd>{selectedDevice.goVersion || '-'}</dd></div>
              <div><dt>CPU / 内存</dt><dd>{selectedDevice.cpuCount || '-'} 核 / {selectedDevice.memoryBytes ? formatBytes(selectedDevice.memoryBytes) : '-'}</dd></div>
              <div><dt>首次 IP</dt><dd>{selectedDevice.firstIp || '-'}</dd></div>
              <div><dt>首次访问</dt><dd>{formatTime(selectedDevice.firstAccess)}</dd></div>
              <div><dt>上次访问</dt><dd>{formatTime(selectedDevice.lastAccess)}</dd></div>
            </div>
            <h4 className="section-title">插件操作记录</h4>
            <Table headers={['插件', '操作', '结果', 'IP', '时间']} rows={deviceLogs.map((log) => [log.pluginId || '-', log.action, log.success ? '成功' : '失败', log.ip || '-', formatTime(log.createdAt)])} />
          </section>
        </div>
      ) : null}

      {passwordModalOpen ? (
        <div className="modal-backdrop" onMouseDown={() => setPasswordModalOpen(false)}>
          <section className="modal small-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>修改密码</h3>
                <p>修改后会写回插件管理后台配置文件。</p>
              </div>
              <button className="icon-button" onClick={() => setPasswordModalOpen(false)} aria-label="关闭"><X size={18} /></button>
            </div>
            <div className="form-stack">
              <label>
                <span>原密码</span>
                <input
                  value={passwordForm.oldPassword}
                  onChange={(event) => setPasswordForm({...passwordForm, oldPassword: event.target.value})}
                  type="password"
                  autoComplete="current-password"
                />
              </label>
              <label>
                <span>新密码</span>
                <input
                  value={passwordForm.newPassword}
                  onChange={(event) => setPasswordForm({...passwordForm, newPassword: event.target.value})}
                  type="password"
                  autoComplete="new-password"
                />
              </label>
              <label>
                <span>确认新密码</span>
                <input
                  value={passwordForm.confirmPassword}
                  onChange={(event) => setPasswordForm({...passwordForm, confirmPassword: event.target.value})}
                  type="password"
                  autoComplete="new-password"
                />
              </label>
            </div>
            <div className="button-row right">
              <button className="secondary" onClick={() => setPasswordModalOpen(false)}>取消</button>
              <button className="primary" onClick={savePassword}>保存</button>
            </div>
          </section>
        </div>
      ) : null}

      {drawerPlugin ? (
        <div className="drawer-backdrop" onMouseDown={closeDrawer}>
          <aside className="drawer" onMouseDown={(event) => event.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-plugin-heading">
                {drawerPlugin.icon ? <img src={drawerPlugin.icon} alt="" /> : <span><Package size={20} /></span>}
                <div>
                  <div className="drawer-title-row"><h3>{drawerPlugin.name}</h3><Status status={drawerPlugin.status} /></div>
                  <p>{drawerPlugin.id} · {drawerPlugin.runtime} · {drawerPlugin.author || '官方'}</p>
                </div>
              </div>
              <button className="icon-button" onClick={closeDrawer} aria-label="关闭"><X size={18} /></button>
            </div>
            <div className="tabs">
              <button className={detailTab === 'detail' ? 'active' : ''} onClick={() => setDetailTab('detail')}>详情</button>
              <button className={detailTab === 'versions' ? 'active' : ''} onClick={() => setDetailTab('versions')}>版本 <span>{versions.length}</span></button>
              <button className={detailTab === 'codes' ? 'active' : ''} onClick={() => setDetailTab('codes')}>激活码 <span>{activationCodes.length}</span></button>
            </div>

            {detailTab === 'detail' ? (
              <div className="drawer-body">
                <div className="drawer-stat-grid">
                  <div><span>评分</span><strong>{(drawerPlugin.ratingAverage || 5).toFixed(1)}</strong><em>{drawerPlugin.ratingCount || 0} 条评价</em></div>
                  <div><span>下载量</span><strong>{(drawerPlugin.downloadCount || 0).toLocaleString()}</strong><em>累计安装</em></div>
                  <div><span>商业模式</span><strong>{drawerPlugin.billingType === 'paid' ? '收费' : '免费'}</strong><em>{categoryLabel(drawerPlugin.group || drawerPlugin.category)}</em></div>
                </div>
                <section className="drawer-section">
                  <div className="drawer-section-title"><h4>基础信息</h4></div>
                  <dl className="detail-grid">
                    <div><dt>插件 ID</dt><dd>{drawerPlugin.id}</dd></div>
                    <div><dt>Runtime</dt><dd>{drawerPlugin.runtime}</dd></div>
                    <div><dt>作者</dt><dd>{drawerPlugin.author || '官方'}</dd></div>
                    <div><dt>分组</dt><dd>{categoryLabel(drawerPlugin.group || drawerPlugin.category)}</dd></div>
                    <div><dt>创建时间</dt><dd>{formatTime(drawerPlugin.createdAt)}</dd></div>
                    <div><dt>更新时间</dt><dd>{formatTime(drawerPlugin.updatedAt)}</dd></div>
                  </dl>
                </section>
                <section className="drawer-section">
                  <div className="drawer-section-title"><h4>市场内容</h4></div>
                  <div className="drawer-copy-block"><span>简介</span><p>{drawerPlugin.summary || '暂无简介'}</p></div>
                  <div className="drawer-copy-block"><span>详细介绍</span><p>{drawerPlugin.description || '暂无详细介绍'}</p></div>
                  <div className="drawer-preview-block"><span>功能预览</span><PreviewImages raw={drawerPlugin.previews || ''} /></div>
                </section>
                <div className="drawer-footer-actions">
                  <button className="primary" onClick={() => setStatus(drawerPlugin, nextPluginStatus(drawerPlugin.status))}>{pluginStatusAction(drawerPlugin.status)}</button>
                </div>
              </div>
            ) : null}

            {detailTab === 'versions' ? (
              <div className="drawer-body">
                <div className="drawer-tools version-toolbar">
                  <div><h4>版本与平台产物</h4><p>同一版本可以包含多个操作系统和架构构建。</p></div>
                  <button className="primary" onClick={() => fileRef.current?.click()}><UploadCloud size={16} /> 上传版本</button>
                </div>
                <div className="version-list">
                  {versions.length ? versions.map((item) => (
                    <article className="version-card" key={item.id}>
                      <div className="version-card-head">
                        <div><span className="version-number">v{item.version}</span><Status status={item.status} /><em>{item.runtime}</em></div>
                        <div className="table-actions">
                          <button className="secondary small" onClick={() => setVersionStatus(item.version, item.status === 'published' ? 'unlisted' : 'published')}>
                            {item.status === 'published' ? '下架' : '发布'}
                          </button>
                          <button className="danger small" onClick={() => deleteVersion(item.version)}>删除</button>
                        </div>
                      </div>
                      <div className="artifact-list">
                        {item.artifacts?.length ? item.artifacts.map((artifact) => (
                          <div className="artifact-row" key={artifact.id}>
                            <span className="artifact-platform"><Package size={14} /> {artifact.os}/{artifact.arch}</span>
                            <span>{formatBytes(artifact.sizeBytes)}</span>
                            <code>{shortHash(artifact.checksum)}</code>
                            <em>{formatTime(artifact.updatedAt)}</em>
                          </div>
                        )) : <div className="artifact-empty">该版本暂无平台构建产物</div>}
                      </div>
                    </article>
                  )) : <div className="version-empty"><PackagePlus size={24} /><strong>暂无版本</strong><span>上传第一个 .wxplugin 安装包开始发布。</span></div>}
                </div>
              </div>
            ) : null}

            {detailTab === 'codes' ? (
              <div className="drawer-body">
                <div className="drawer-tools version-toolbar code-toolbar">
                  <div><h4>激活码管理</h4><p>批量生成收费插件的授权凭证，并追踪激活状态。</p></div>
                  <div className="code-generator">
                    <label className="code-count-field"><span>生成数量</span><input value={codeCount} onChange={(event) => setCodeCount(event.target.value)} inputMode="numeric" aria-label="生成数量" /></label>
                    <button className="primary" onClick={generateCodes}><Ticket size={16} /> 生成激活码</button>
                  </div>
                </div>
                <div className="activation-list">
                  {activationCodes.length ? pagedActivationCodes.map((item) => (
                    <article className="activation-card" key={item.id}>
                      <div className="activation-code-block"><span>激活码</span><code>{item.code}</code></div>
                      <div className="activation-meta"><span className={`activation-status ${item.status}`}>{item.status === 'activated' ? '已激活' : item.status === 'revoked' ? '已撤销' : '未使用'}</span><div><small>激活范围</small><strong>{scopeLabel(item.redeemedScopeType || (item.activatedAccountKey ? 'deployment' : ''))}</strong></div><div><small>激活对象</small><strong>{item.redeemedScopeKey || item.activatedAccountKey || '-'}</strong></div><div><small>更新时间</small><strong>{formatTime(item.updatedAt)}</strong></div></div>
                    </article>
                  )) : <div className="version-empty"><Ticket size={24} /><strong>暂无激活码</strong><span>输入生成数量，创建第一批授权凭证。</span></div>}
                  {activationCodes.length ? <Pagination page={activationCodePage} pageCount={activationCodePageCount} total={activationCodes.length} onChange={setActivationCodePage} /> : null}
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}
    </main>
  )
}

function Status({status}: {status: string}) {
  return <span className={`status ${status}`}>{status}</span>
}

function MetricCard({icon, tone, label, value, hint}: {icon: ReactNode; tone: string; label: string; value: number; hint: string}) {
  return (
    <article className="metric-card">
      <span className={`metric-icon ${tone}`}>{icon}</span>
      <div className="metric-copy"><span>{label}</span><strong>{value.toLocaleString()}</strong><em>{hint}</em></div>
    </article>
  )
}

function PluginActionsMenu({
  plugin,
  open,
  onToggle,
  onClose,
  onDetail,
  onEdit,
  onSetStatus,
  onDelete,
}: {
  plugin: Plugin
  open: boolean
  onToggle: () => void
  onClose: () => void
  onDetail: () => void
  onEdit: () => void
  onSetStatus: () => void
  onDelete: () => void
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [menuPosition, setMenuPosition] = useState({top: 0, left: 0})

  useEffect(() => {
    if (!open) return
    function updatePosition() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const menuWidth = 144
      const menuHeight = 156
      const left = Math.max(12, Math.min(window.innerWidth - menuWidth - 12, rect.right - menuWidth))
      const top = rect.bottom + menuHeight + 12 > window.innerHeight ? rect.top - menuHeight - 6 : rect.bottom + 6
      setMenuPosition({top, left})
    }
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open])

  function run(action: () => void) {
    action()
    onClose()
  }

  return (
    <div className="actions-menu">
      <button ref={triggerRef} className="secondary small actions-trigger" onClick={onToggle}>
        <MoreHorizontal size={15} />
        更多
      </button>
      {open ? createPortal(
        <>
          <button type="button" className="actions-clickaway" onClick={onClose} aria-label="关闭操作菜单" />
          <div className="actions-dropdown actions-dropdown-portal" style={menuPosition}>
            <button onClick={() => run(onDetail)}><Eye size={14} /> 详情</button>
            <button onClick={() => run(onEdit)}><Pencil size={14} /> 编辑</button>
            <button onClick={() => run(onSetStatus)}><Archive size={14}/> {pluginStatusAction(plugin.status)}</button>
            <button className="danger-item" onClick={() => run(onDelete)}><Trash2 size={14} /> 删除</button>
          </div>
        </>,
        document.body,
      ) : null}
    </div>
  )
}

function avatarText(value: string) {
  const text = value.trim()
  return (text[0] || 'A').toUpperCase()
}

function nextPluginStatus(status: string): 'listed' | 'unlisted' {
  return status === 'listed' ? 'unlisted' : 'listed'
}

function pluginStatusAction(status: string) {
  return status === 'listed' ? '下架' : '上架'
}

function categoryLabel(value?: string) {
  return categoryOptions.find((item) => item.value === value)?.label || '实用工具'
}

function scopeLabel(value?: string) {
  return value === 'deployment' ? '私有化部署' : value || '-'
}

function sourceLabel(value?: string) {
  if (value === 'activation_code') return '激活码'
  if (value === 'admin_grant') return '后台授权'
  if (value === 'purchase') return '购买'
  return value || '-'
}

function Table({headers, rows, className = ''}: {headers: string[]; rows: ReactNode[][]; className?: string}) {
  return (
    <div className={`table-wrap ${className}`}>
      <table>
        <thead>
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, index) => (
            <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
          )) : (
            <tr><td colSpan={headers.length} className="empty-cell">暂无数据</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function Pagination({page, pageCount, total, onChange}: {page: number; pageCount: number; total: number; onChange: (page: number) => void}) {
  if (total <= 5) return null
  return <div className="pagination"><span>共 {total} 条</span><button className="secondary" disabled={page <= 1} onClick={() => onChange(page - 1)}>上一页</button><span>{page} / {pageCount}</span><button className="secondary" disabled={page >= pageCount} onClick={() => onChange(page + 1)}>下一页</button></div>
}

function shortHash(value: string) {
  return value ? `${value.slice(0, 10)}...` : '-'
}

function formatBytes(value: number) {
  if (!value) return '-'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / 1024 / 1024).toFixed(1)} MB`
}

function formatTime(value: number) {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}
